import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/pro-regular-svg-icons/faTimesCircle';
import CorgixIcon from '@veeva/corgix/icon';
import PropTypes from 'prop-types';
import useMessageService from '../services/i18n/useMessageService';
import InvalidPasswordCharactersInlineMessage from './InvalidPasswordCharactersInlineMessage';

const propTypes = {
    /**
     * Callback fired when the button is clicked. Invoked with:
     * `onStateChange({ valid: {Boolean}, inlineError: {React element} })`
     */
    onStateChange: PropTypes.func,

    /**
     * Password
     */
    password: PropTypes.string,

    /**
     * Password confirmation
     */
    passwordConfirmation: PropTypes.string,
};

// ASCII (32 to 126)
export function onlyContainsValidAsciiCharacters(passwordText) {
    if (passwordText) {
        // Return true if password does not contain a character outside 32 to 126 range
        return !/[^\x20-\x7E]/g.test(passwordText);
    }
    return true;
}

export const hasLowerCase = (str) => {
    return /[a-z]/.test(str);
};

export const hasUpperCase = (str) => {
    return /[A-Z]/.test(str);
};

export const hasDigit = (str) => {
    return /\d/.test(str);
};

const Criteria = ({ criteriaMet, message }) => {
    return (
        <li
            css={css`
                display: flex;
                padding: 0 0.3333rem 0.3333rem 0;
                align-items: center;
                font-size: 1rem;
                :last-child {
                    padding-bottom: 0;
                }

                > svg {
                    padding-right: 0.3333rem;
                }
            `}
        >
            {criteriaMet && <CorgixIcon color="#539137" type={faCheckCircle} size="xs" />}
            {!criteriaMet && <CorgixIcon color="#ce1300" type={faTimesCircle} size="xs" />}
            <span>{message}</span>
        </li>
    );
};

const PasswordRequirementsValidator = ({ password, passwordConfirmation, onStateChange }) => {
    const [
        [
            passwordRequirementsLabelMinLength = 'Password must be eight or more characters long',
            passwordRequirementsLabelUppercase = 'Must have one uppercase letter',
            passwordRequirementsLabelLowercaseChars = 'Must have one lowercase letter',
            passwordRequirementsLabelNumberChars = 'Must have one number',
            passwordRequirementsLabelMatch = 'Passwords must match',
        ],
    ] = useMessageService([
        'password_requirements.min_length',
        'password_requirements.uppercase',
        'password_requirements.lowercase_chars',
        'password_requirements.number_chars',
        'password_requirements.match',
    ]);

    const criterias = useMemo(
        () => ({
            minLength: password.length >= 8,
            lowercase: hasLowerCase(password),
            uppercase: hasUpperCase(password),
            number: hasDigit(password),
            passwordMatch: password && password === passwordConfirmation,
            validAsciiChars: onlyContainsValidAsciiCharacters(password),
        }),
        [password, passwordConfirmation],
    );

    const anyFailed = useMemo(
        () => Object.values(criterias ?? {}).some((curStatus) => !curStatus),
        [criterias],
    );

    const inlineError = useMemo(() => <InvalidPasswordCharactersInlineMessage show />, []);

    useEffect(() => {
        onStateChange?.({
            valid: !anyFailed,
            inlineError: criterias.validAsciiChars ? undefined : inlineError,
        });
    }, [onStateChange, anyFailed, criterias.validAsciiChars, inlineError]);

    const containerCss = css`
        width: 100%;
    `;

    return (
        <div css={containerCss}>
            <ul
                css={css`
                    list-style: none;
                    padding: 0;
                    margin: 0;
                `}
            >
                <Criteria
                    criteriaMet={criterias.minLength}
                    message={passwordRequirementsLabelMinLength}
                />
                <Criteria
                    criteriaMet={criterias.uppercase}
                    message={passwordRequirementsLabelUppercase}
                />
                <Criteria
                    criteriaMet={criterias.lowercase}
                    message={passwordRequirementsLabelLowercaseChars}
                />
                <Criteria
                    criteriaMet={criterias.number}
                    message={passwordRequirementsLabelNumberChars}
                />
                <Criteria
                    criteriaMet={criterias.passwordMatch}
                    message={passwordRequirementsLabelMatch}
                />
            </ul>
        </div>
    );
};

PasswordRequirementsValidator.propTypes = propTypes;

export default PasswordRequirementsValidator;
